import { useState, useRef, useEffect } from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Pencil, RotateCcw, UserPlus, Lock, Unlock, MoreVertical } from 'lucide-react';

/* --- Desktop table row --------------------------------------------------- */
function GroupNumberRow({ groupNumber, userName, disComm, groupStatus, isPrimary, userId, fullName, p_FullName, gC_Affiliate, toggle_add_edit_popup, toggle_reset_popup, restrict_release }) {
    return (
        <>
            {groupNumber ? (
                <TableRow>
                    <TableCell className="font-medium">{groupNumber}</TableCell>
                    <TableCell>{fullName || '-'}</TableCell>
                    <TableCell>{p_FullName || '-'}</TableCell>
                    <TableCell>{gC_Affiliate || '-'}</TableCell>
                    <TableCell>{disComm || '-'}</TableCell>
                    <TableCell>
                        <StatusBadge status={groupStatus} />
                    </TableCell>
                    <TableCell>
                        <TooltipProvider>
                            <div className="flex items-center gap-2">
                                <ActionButtons
                                    groupStatus={groupStatus}
                                    groupNumber={groupNumber}
                                    userName={userName}
                                    userId={userId}
                                    isPrimary={isPrimary}
                                    toggle_add_edit_popup={toggle_add_edit_popup}
                                    toggle_reset_popup={toggle_reset_popup}
                                    restrict_release={restrict_release}
                                />
                            </div>
                        </TooltipProvider>
                    </TableCell>
                </TableRow>
            ) : (
                <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                        No Record Found
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}

/* --- Mobile card ---------------------------------------------------------- */
export function GroupNumberCard({ groupNumber, userName, disComm, groupStatus, isPrimary, userId, fullName, p_FullName, gC_Affiliate, toggle_add_edit_popup, toggle_reset_popup, restrict_release }) {
    const [menuOpen, setMenuOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!menuOpen) return;

        // mousedown / touchstart fire only on an actual press � never on
        // cursor movement � so the menu stays open while the user moves around.
        const handleOutside = (e) => {
            if (!wrapperRef.current?.contains(e.target)) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);

        return () => {
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
    }, [menuOpen]);

    if (!groupNumber) return null;

    const handleItem = (fn) => (e) => {
        setMenuOpen(false);
        fn(e);
    };

    return (
        <div className="bg-card rounded-lg border border-border shadow-sm p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
                <div className="grid grid-cols-2 flex-1 gap-x-4 gap-y-3">
                    <div>
                        <p className="text-xs text-muted-foreground">Group Number</p>
                        <p className="text-sm font-semibold text-accent">{groupNumber}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Full Name</p>
                        <p className="text-sm font-semibold text-accent">{fullName || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Parent Affiliate</p>
                        <p className="text-sm">{p_FullName || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Grandparent Affiliate</p>
                        <p className="text-sm">{gC_Affiliate || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Commission($)</p>
                        <p className="text-sm">{disComm || '-'}</p>
                    </div>
                    <div>
                        <p className="text-xs text-muted-foreground">Status</p>
                        <StatusBadge status={groupStatus} />
                    </div>
                </div>

                {/* Three-dot action menu */}
                <div className="relative shrink-0" ref={wrapperRef}>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 -mt-1"
                        onClick={() => setMenuOpen(o => !o)}
                    >
                        <MoreVertical className="h-4 w-4" />
                    </Button>

                    {menuOpen && (
                        <div className="absolute right-0 top-full mt-1 z-50 min-w-[8rem] rounded-md border bg-popover shadow-md p-1">
                            {groupStatus === 'Restricted' && (
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start gap-2 px-2 py-1.5 h-auto font-normal text-sm"
                                    onClick={handleItem(restrict_release)}
                                    data-tagname="Release"
                                    data-groupumber={groupNumber}
                                >
                                    <Unlock className="h-4 w-4" /> Release
                                </Button>
                            )}

                            {groupStatus === 'Assigned' && (
                                <>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start gap-2 px-2 py-1.5 h-auto font-normal text-sm"
                                        onClick={handleItem(toggle_add_edit_popup)}
                                        data-userid={userId}
                                        data-groupumber={groupNumber}
                                        data-poptype="edit"
                                    >
                                        <Pencil className="h-4 w-4" /> Edit
                                    </Button>
                                    {isPrimary !== true && (
                                        <Button
                                            variant="ghost"
                                            className={cn("w-full justify-start gap-2 px-2 py-1.5 h-auto font-normal text-sm", isPrimary ? 'true' : 'false')}
                                            onClick={handleItem(toggle_reset_popup)}
                                            data-groupumber={groupNumber}
                                            data-username={userName}
                                        >
                                            <RotateCcw className="h-4 w-4" /> Reset
                                        </Button>
                                    )}
                                </>
                            )}

                            {groupStatus === 'Unassigned' && (
                                <>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start gap-2 px-2 py-1.5 h-auto font-normal text-sm"
                                        onClick={handleItem(toggle_add_edit_popup)}
                                        data-groupumber={groupNumber}
                                        data-poptype="assign"
                                    >
                                        <UserPlus className="h-4 w-4" /> Assign
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        className="w-full justify-start gap-2 px-2 py-1.5 h-auto font-normal text-sm"
                                        onClick={handleItem(restrict_release)}
                                        data-tagname="Restrict"
                                        data-groupumber={groupNumber}
                                    >
                                        <Lock className="h-4 w-4" /> Restrict
                                    </Button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

/* --- Shared helpers ------------------------------------------------------- */
function StatusBadge({ status }) {
    return (
        <Badge variant={
            status === 'Assigned'   ? 'success' :
            status === 'Unassigned' ? 'secondary' :
                                      'destructive'
        }>
            {status}
        </Badge>
    );
}

function ActionButtons({ groupStatus, groupNumber, userName, userId, isPrimary, toggle_add_edit_popup, toggle_reset_popup, restrict_release }) {
    if (groupStatus === 'Restricted') {
        return (
            <Tooltip>
                <TooltipTrigger asChild>
                    <Button variant="ghost" size="sm" onClick={restrict_release} data-tagname="Release" data-groupumber={groupNumber}>
                        <Unlock className="h-4 w-4" />
                    </Button>
                </TooltipTrigger>
                <TooltipContent><p>Release</p></TooltipContent>
            </Tooltip>
        );
    }

    if (groupStatus === 'Assigned') {
        return (
            <>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="sm" onClick={toggle_add_edit_popup}
                            data-userid={userId} data-groupumber={groupNumber} data-poptype="edit"
                            className="hover:bg-transparent group">
                            <Pencil className="h-4 w-4 transition-colors text-muted-foreground group-hover:text-accent" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent><p>Edit</p></TooltipContent>
                </Tooltip>
                {isPrimary !== true && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={toggle_reset_popup}
                                data-groupumber={groupNumber} data-username={userName}
                                className={`hover:bg-transparent group ${isPrimary ? 'true' : 'false'}`}>
                                <RotateCcw className="h-4 w-4 transition-colors text-muted-foreground group-hover:text-accent" />
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent><p>Reset</p></TooltipContent>
                    </Tooltip>
                )}
            </>
        );
    }

    if (groupStatus === 'Unassigned') {
        return (
            <>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="sm" onClick={toggle_add_edit_popup}
                            data-groupumber={groupNumber} data-poptype="assign">
                            <UserPlus className="h-4 w-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent><p>Assign</p></TooltipContent>
                </Tooltip>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="sm" onClick={restrict_release}
                            data-tagname="Restrict" data-groupumber={groupNumber}>
                            <Lock className="h-4 w-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent><p>Restrict</p></TooltipContent>
                </Tooltip>
            </>
        );
    }

    return null;
}

export default GroupNumberRow;


